/** Org-admin action audit log — role/member/config changes with actor +
 * timestamp. Read-only oversight surface; consumes the paginated
 * GET /v1/orgs/admin-audit. Closes the "who changed a role/member/config
 * and when" gap the permission-bleed incident exposed. */
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import type { Paginated } from "@/lib/types";

type AuditEvent = {
  id: string;
  action: string;
  actor_email: string;
  target_type: string;
  target_label: string;
  summary: string;
  metadata_jsonb: Record<string, unknown>;
  created_at: string;
};

const ACTIONS = [
  "role.created",
  "role.updated",
  "role.deleted",
  "member.roles_changed",
  "org.updated",
];

export function OrgAdminAuditLog() {
  const { t } = useTranslation();
  const [action, setAction] = useState("");
  const q = useQuery({
    queryKey: ["org-admin-audit", action],
    queryFn: async () =>
      (
        await api.get<Paginated<AuditEvent>>(ApiPaths.orgAdminAudit, {
          params: action ? { action } : undefined,
        })
      ).data,
  });

  // Tamper-evidence: recompute the per-org hash chain and show its state.
  const integrity = useQuery({
    queryKey: ["org-admin-audit-verify"],
    queryFn: async () =>
      (
        await api.get<{ ok: boolean; total: number; broken: unknown }>(
          ApiPaths.orgAdminAuditVerify,
        )
      ).data,
  });

  const rows = q.data?.results ?? [];

  return (
    <section className="card space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold">
              {t("admin.auditLog.title", "Admin action log")}
            </h2>
            {integrity.data && (
              <span
                className={
                  "rounded-full px-2 py-0.5 text-[10px] font-medium " +
                  (integrity.data.ok
                    ? "bg-emerald-500/15 text-emerald-300"
                    : "bg-cvp-danger/20 text-cvp-danger")
                }
                title={t(
                  "admin.auditLog.integrityTitle",
                  "Tamper-evident hash chain",
                )}
              >
                {integrity.data.ok
                  ? t("admin.auditLog.verified", {
                      count: integrity.data.total,
                      defaultValue: "✓ Chain verified ({{count}})",
                    })
                  : t("admin.auditLog.tampered", "⚠ Chain broken")}
              </span>
            )}
          </div>
          <p className="text-xs text-nb-text-3">
            {t(
              "admin.auditLog.lead",
              "Who changed roles, members, or org settings — and when.",
            )}
          </p>
        </div>
        <select
          className="input h-8 w-52 max-w-full text-sm"
          value={action}
          onChange={(e) => setAction(e.target.value)}
          aria-label={t("admin.auditLog.filterAction", "Filter by action")}
        >
          <option value="">{t("admin.auditLog.allActions", "All actions")}</option>
          {ACTIONS.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
      </div>

      {q.isLoading ? (
        <p className="text-sm text-nb-text-2">{t("common.loading")}</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-nb-text-3">
          {t("admin.auditLog.empty", "No admin actions recorded yet.")}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-xs uppercase text-nb-text-3">
              <tr>
                <th className="py-1 pr-3">{t("admin.auditLog.when", "When")}</th>
                <th className="py-1 pr-3">{t("admin.auditLog.actor", "Actor")}</th>
                <th className="py-1 pr-3">{t("admin.auditLog.action", "Action")}</th>
                <th className="py-1">{t("admin.auditLog.detail", "Detail")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id} className="border-t border-nb-border/50 align-top">
                  <td className="whitespace-nowrap py-1.5 pr-3 text-nb-text-3">
                    {formatDateTime(e.created_at)}
                  </td>
                  <td className="py-1.5 pr-3 text-nb-text-2">
                    {e.actor_email || "—"}
                  </td>
                  <td className="py-1.5 pr-3">
                    <code className="text-nb-mint">{e.action}</code>
                  </td>
                  <td className="py-1.5 text-nb-text">{e.summary}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {q.data && q.data.count > rows.length && (
            <p className="mt-2 text-xs text-nb-text-3">
              {t("admin.auditLog.showingRecent", {
                shown: rows.length,
                count: q.data.count,
                defaultValue: "Showing the {{shown}} most recent of {{count}}.",
              })}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
